import React, { useState, useCallback, useEffect, createRef } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { FlexContainer, PureSpinner, styledTheme } from 'heliocor-ui'
import 'styled-components/macro'

import { REQUEST_ACTIVATION }from '../../store/cafe/actionTypes'
import { FadeIn } from '../../common/animations'
import { CircularProg } from '../../common/animations';
import { CheckMark, XMark, InputBlock } from './CodeTextField.styles'

const CodeTextField = ({codeFields = 6}) => {
    
    const codeRefs = Array.from(Array(codeFields).keys()).map(() => createRef())
    const dispatch = useDispatch()
    const [code, updateCode] = useState(Array(codeFields).fill(''))
    const [index, setIndex] = useState(0)

    const authenticated = useSelector(store => store.config.authenticated)
    const token = useSelector(store => store.config.token)
    const cafeStatus = useSelector(store => store.general.cafeStatus)
    const partyLoaded = cafeStatus === 'PARTY_LOADED'

    const codeInserted = code.every(field => field !== '')
    const activateCafe = useCallback((activationCode) => dispatch({ type: REQUEST_ACTIVATION, activationCode }), [dispatch])

    const inputChangeHandler = (index, e) => {

        if(isNaN(Number(e.target.value.slice(-1))) || !e.target || !e.target.value) return

        let updatedCode = [...code]
        updatedCode[index] = parseInt(e.target.value)
        updateCode(updatedCode)
        
        setIndex(index !== 5 ? index+1 : 0)
    }

    useEffect(() => { 
        if (index >= 0) codeRefs[index].current.focus() 
    }, [index, code, codeRefs])

    useEffect(() => {
        if (codeInserted && !authenticated) {
            activateCafe(code.join(''))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activateCafe, codeInserted, code])

    useEffect(() => {
        if (!authenticated && codeInserted && !token && !partyLoaded){ 
            const timer = setTimeout(() => {
                updateCode(Array(6).fill(''))
                clearTimeout(timer)
            }, 3000)
        }
    }, [token, codeInserted, !!authenticated, dispatch, partyLoaded])

    const onKeyDownHandler = (e) => {
        const invalidChars = [69, 187, 189, 188, 190, 229]
        if (invalidChars.includes(e.keyCode)) e.preventDefault()
        let updatedCode = [...code]
        if (e.keyCode === 8) {
            updatedCode[index-1] = ''
            updateCode(updatedCode)
            if (index >= 1) setIndex(index - 1) 
            else setIndex(0)
        } 
    }

    const onFocusCleanUp = (newIndex) => {
        
        if(newIndex < index) {
            let fieldsToClean = codeFields - newIndex
            let newCode = [...code].slice(0, newIndex)
            let emptyStringsToAdd = new Array(fieldsToClean).fill('')
            let filledArray = [...newCode, ...emptyStringsToAdd]
            updateCode(filledArray)
            setIndex(newIndex)
        }
    }

    return (
        <FlexContainer alignItems='center'>
            {code.map((input,i) => <InputBlock
                type='number'
                disableUnderline
                key={i}
                inputRef={codeRefs[i]}
                disabled={codeInserted}
                value={code[i]}
                onInput={(e) => inputChangeHandler(i, e)}
                onKeyDown={onKeyDownHandler}
                onFocus={() => onFocusCleanUp(i)}
                data-cy='inputCode'
            />)}
            <FadeIn showItem={codeInserted} >
                {!partyLoaded 
                    ? <div css='padding-bottom: 4px'><PureSpinner color='grey300' strokeWidth={3.8} size='28px' timing='6.5s'/></div>
                    : <CircularProg color={authenticated  ? styledTheme.colors.aqua100 : styledTheme.colors.red100}>
                        { authenticated  ?  <CheckMark viewBox="-6 -4 30 24" sizePx="30"/>  : <XMark viewBox="-11 -11 30 30" sizePx="24"/>}
                    </CircularProg>  
                }
            </FadeIn>
        </FlexContainer>
    )
}

export default CodeTextField